import { compareText } from './compareText'
import type {
  ActivityEntry,
  ActivityGroup,
  ActivityQuery,
  ActivityTotals,
  ActivityType,
  ActivityView,
  DateWindow,
  GroupCounts,
  MoneyFigure,
  MonthKey,
} from './types'

export const ACTIVITY_TYPE_LABELS: Record<ActivityType, string> = {
  buy: 'Buy',
  sell: 'Sell',
  unsettled_trade: 'Unsettled trade',
  dividend: 'Dividend',
  reinvestment: 'Reinvestment',
  interest: 'Interest',
  fee: 'Fee',
  deposit: 'Deposit',
  withdrawal: 'Withdrawal',
  transfer_in: 'Transfer in',
  transfer_out: 'Transfer out',
  contribution: 'Contribution',
  distribution: 'Distribution',
  sweep: 'Sweep',
  other: 'Other',
  paired_transfer: 'Paired transfer',
}

export const ACTIVITY_GROUP_LABELS: Record<'all' | ActivityGroup, string> = {
  all: 'All',
  money_movements: 'Money movements',
  trades: 'Trades',
  income: 'Income',
  fees: 'Fees',
  other: 'Other',
}

export const ACTIVITY_GROUP_OF: Record<ActivityType, ActivityGroup> = {
  deposit: 'money_movements',
  withdrawal: 'money_movements',
  transfer_in: 'money_movements',
  transfer_out: 'money_movements',
  contribution: 'money_movements',
  distribution: 'money_movements',
  paired_transfer: 'money_movements',
  buy: 'trades',
  sell: 'trades',
  unsettled_trade: 'trades',
  dividend: 'income',
  interest: 'income',
  reinvestment: 'income',
  fee: 'fees',
  sweep: 'other',
  other: 'other',
}

type AccountFilter = (accountId: string) => boolean

export const accountFilterOf = (accountIds: string[] | null): AccountFilter => {
  if (accountIds == null) return () => true
  const selected = new Set(accountIds)
  return (accountId) => selected.has(accountId)
}

// A transfer between two accounts in view is one event, a paired transfer shown once. With only one
// side's account in view, it is a plain transfer.
const isPairInView = (entry: ActivityEntry, inView: AccountFilter) =>
  entry.pairing != null && inView(entry.pairing.partner.accountId)

export const activityTypeOf = (
  entry: ActivityEntry,
  inView: AccountFilter,
): ActivityType =>
  isPairInView(entry, inView) ? 'paired_transfer' : entry.kind

// The receiving side hides only when its sending row is listed too. When the sending row falls
// outside the dates or filters, the receiving row shows as the paired transfer in its place.
const isPartnerListed = (entry: ActivityEntry, listedIds: Set<string>) =>
  entry.pairing?.role === 'receiver' &&
  listedIds.has(entry.pairing.partner.entryId)

const withoutListedReceivers = (listed: ActivityEntry[]) => {
  const listedIds = new Set(listed.map((entry) => entry.id))
  return listed.filter((entry) => !isPartnerListed(entry, listedIds))
}

// A row with no printed date may fall anywhere in its statement's period, so it is kept when that
// period overlaps the dates.
export const isInDates = (
  entry: Pick<ActivityEntry, 'date' | 'statementPeriod'>,
  dates: { from?: string; to?: string },
) => {
  const { from, to } = dates
  if (!from && !to) return true
  const earliest = entry.date || entry.statementPeriod?.from
  const latest = entry.date || entry.statementPeriod?.to
  if (!earliest || !latest) return false
  return (!from || latest >= from) && (!to || earliest <= to)
}

const isInDateRange = (
  entry: ActivityEntry,
  query: Pick<ActivityQuery, 'dateRange'>,
) => isInDates(entry, query.dateRange ?? {})

const isInAmountRange = (entry: ActivityEntry, query: ActivityQuery) => {
  const { min, max } = query.amount ?? {}
  if (min == null && max == null) return true
  if (entry.amount == null) return false
  return (
    (min == null || entry.amount >= min) && (max == null || entry.amount <= max)
  )
}

const includesText = (value: string, search: string | undefined) =>
  !search || value.toLowerCase().includes(search.trim().toLowerCase())

const matchesRowFilters = (entry: ActivityEntry, query: ActivityQuery) =>
  isInAmountRange(entry, query) &&
  includesText(entry.symbol, query.symbol) &&
  includesText(entry.description, query.description)

const matchesTypeFilters = (type: ActivityType, query: ActivityQuery) =>
  (!query.group ||
    query.group === 'all' ||
    ACTIVITY_GROUP_OF[type] === query.group) &&
  (!query.types || query.types.length === 0 || query.types.includes(type))

const toCents = (amount: number | null) => Math.round((amount ?? 0) * 100)

interface CentsFigure {
  cents: number
  count: number
}

const add = (figure: CentsFigure, entry: ActivityEntry) => {
  figure.cents += toCents(entry.amount)
  figure.count += 1
}

const dollars = (figure: CentsFigure): MoneyFigure => ({
  amount: figure.cents / 100,
  count: figure.count,
})

const newFigures = () => ({
  deposit: { cents: 0, count: 0 },
  contribution: { cents: 0, count: 0 },
  withdrawal: { cents: 0, count: 0 },
  distribution: { cents: 0, count: 0 },
  transfer_in: { cents: 0, count: 0 },
  transfer_out: { cents: 0, count: 0 },
  buy: { cents: 0, count: 0 },
  sell: { cents: 0, count: 0 },
  dividend: { cents: 0, count: 0 },
  interest: { cents: 0, count: 0 },
  fee: { cents: 0, count: 0 },
})

type Figures = ReturnType<typeof newFigures>

const LEFT_OUT: Partial<Record<ActivityType, keyof ActivityTotals['leftOut']>> =
  {
    sweep: 'sweeps',
    unsettled_trade: 'unsettledTrades',
    paired_transfer: 'pairedTransfers',
    reinvestment: 'reinvestments',
  }

const sumFigures = (figures: CentsFigure[]): MoneyFigure =>
  dollars(
    figures.reduce(
      (total, figure) => ({
        cents: total.cents + figure.cents,
        count: total.count + figure.count,
      }),
      { cents: 0, count: 0 },
    ),
  )

interface OtherFigures {
  labeled: CentsFigure
  unsortedIn: CentsFigure
  unsortedOut: CentsFigure
}

const toTotals = (
  figures: Figures,
  commissionCents: { buy: number; sell: number },
  interestCents: { earned: number; charged: number },
  leftOut: ActivityTotals['leftOut'],
  other: OtherFigures,
  rowCount: number,
): ActivityTotals => {
  const moneyCents =
    figures.deposit.cents +
    figures.contribution.cents +
    figures.withdrawal.cents +
    figures.distribution.cents +
    figures.transfer_in.cents +
    figures.transfer_out.cents +
    other.unsortedIn.cents +
    other.unsortedOut.cents
  return {
    rowCount,
    deposits: {
      ...sumFigures([figures.deposit, figures.contribution]),
      depositCount: figures.deposit.count,
      contributionCount: figures.contribution.count,
    },
    withdrawals: {
      ...sumFigures([figures.withdrawal, figures.distribution]),
      withdrawalCount: figures.withdrawal.count,
      distributionCount: figures.distribution.count,
    },
    transfersIn: dollars(figures.transfer_in),
    transfersOut: dollars(figures.transfer_out),
    netMoney: moneyCents / 100,
    bought: { ...dollars(figures.buy), commissions: commissionCents.buy / 100 },
    sold: { ...dollars(figures.sell), commissions: commissionCents.sell / 100 },
    commissions: (commissionCents.buy + commissionCents.sell) / 100,
    dividends: dollars(figures.dividend),
    interest: {
      ...dollars(figures.interest),
      earned: interestCents.earned / 100,
      charged: interestCents.charged / 100,
    },
    fees: dollars(figures.fee),
    leftOut,
    other: dollars(other.labeled),
    unsortedIn: dollars(other.unsortedIn),
    unsortedOut: dollars(other.unsortedOut),
  }
}

const carriesMoney = (entry: ActivityEntry) =>
  entry.amount != null && entry.amount !== 0

const addOther = (other: OtherFigures, entry: ActivityEntry) => {
  if (!carriesMoney(entry)) return
  if (entry.unsortedDirection === 'in') add(other.unsortedIn, entry)
  else if (entry.unsortedDirection === 'out') add(other.unsortedOut, entry)
  else add(other.labeled, entry)
}

// Rows that moved money in or out of the accounts, unsorted ledger rows included.
export const moneyMovementCount = (totals: ActivityTotals): number =>
  totals.deposits.count +
  totals.withdrawals.count +
  totals.transfersIn.count +
  totals.transfersOut.count +
  totals.unsortedIn.count +
  totals.unsortedOut.count

// The totals strip: money in and out, trades, income and fees over the visible rows given. A paired
// transfer, a sweep, an unsettled trade and a reinvestment move no money in or out, so they are
// counted as left out instead.
export const totalActivity = (
  entries: ActivityEntry[],
  inView: AccountFilter,
): ActivityTotals => {
  const figures = newFigures()
  const commissionCents = { buy: 0, sell: 0 }
  const interestCents = { earned: 0, charged: 0 }
  const leftOut = {
    sweeps: 0,
    unsettledTrades: 0,
    pairedTransfers: 0,
    reinvestments: 0,
  }
  const other: OtherFigures = {
    labeled: { cents: 0, count: 0 },
    unsortedIn: { cents: 0, count: 0 },
    unsortedOut: { cents: 0, count: 0 },
  }
  const listed = withoutListedReceivers(
    entries.filter((entry) => inView(entry.accountId)),
  )
  for (const entry of listed) {
    const type = activityTypeOf(entry, inView)
    const leftOutKey = LEFT_OUT[type]
    if (leftOutKey) {
      leftOut[leftOutKey] += 1
      continue
    }
    if (type === 'other') addOther(other, entry)
    if (!(type in figures)) continue
    add(figures[type as keyof Figures], entry)
    if (type === 'buy' || type === 'sell') {
      commissionCents[type] += Math.abs(toCents(entry.commission))
    }
    if (type === 'interest') {
      const cents = toCents(entry.amount)
      if (cents >= 0) interestCents.earned += cents
      else interestCents.charged += cents
    }
  }
  return toTotals(
    figures,
    commissionCents,
    interestCents,
    leftOut,
    other,
    listed.length,
  )
}

const emptyGroupCounts = (): GroupCounts => ({
  all: 0,
  money_movements: 0,
  trades: 0,
  income: 0,
  fees: 0,
  other: 0,
})

type Comparator = (first: ActivityEntry, second: ActivityEntry) => number

const compareNullableNumbers = (
  first: number | null,
  second: number | null,
) => {
  if (first == null && second == null) return 0
  if (first == null) return 1
  if (second == null) return -1
  return first - second
}

// The date a row is placed by: its own, or its statement's end when none was printed.
export const placementDate = (
  entry: Pick<ActivityEntry, 'date' | 'statementPeriod'>,
): string => entry.date || entry.statementPeriod?.to || ''

const listMonthOf = (entry: ActivityEntry) => placementDate(entry).slice(0, 7)

const isUndated = (entry: ActivityEntry) => Number(!entry.date)

// A row with no printed date is listed after the dated rows of its statement's end month, whichever
// way the dates run.
const byDate =
  (sign: number): Comparator =>
  (first, second) =>
    sign * compareText(listMonthOf(first), listMonthOf(second)) ||
    isUndated(first) - isUndated(second) ||
    sign * compareText(first.date, second.date)

const comparatorFor = (
  query: ActivityQuery,
  inView: AccountFilter,
): Comparator => {
  const { field, direction } = query.sort ?? {
    field: 'date',
    direction: 'desc',
  }
  const sign = direction === 'asc' ? 1 : -1
  const byField: Record<string, Comparator> = {
    amount: (first, second) =>
      compareNullableNumbers(first.amount, second.amount),
    account: (first, second) =>
      compareText(first.accountName, second.accountName),
    symbol: (first, second) => compareText(first.symbol, second.symbol),
    type: (first, second) =>
      compareText(
        ACTIVITY_TYPE_LABELS[activityTypeOf(first, inView)],
        ACTIVITY_TYPE_LABELS[activityTypeOf(second, inView)],
      ),
  }
  const compare =
    field === 'date'
      ? byDate(sign)
      : (first: ActivityEntry, second: ActivityEntry) =>
          sign * byField[field](first, second)
  return (first, second) =>
    compare(first, second) || compareText(first.id, second.id)
}

const dateSpanOf = (entries: ActivityEntry[]): DateWindow | null => {
  const dates = entries.map((entry) => entry.date).filter(Boolean)
  if (dates.length === 0) return null
  return {
    from: dates.reduce((first, date) => (date < first ? date : first)),
    to: dates.reduce((last, date) => (date > last ? date : last)),
  }
}

// Filter, count, total and sort the merged list in one pass over the rows.
export const buildActivityView = (
  entries: ActivityEntry[],
  query: ActivityQuery,
): ActivityView => {
  const inView = accountFilterOf(query.accountIds)
  const rows: ActivityEntry[] = []
  const groupCounts = emptyGroupCounts()
  const typeCounts: Partial<Record<ActivityType, number>> = {}
  const listed = withoutListedReceivers(
    entries.filter(
      (entry) =>
        inView(entry.accountId) &&
        isInDateRange(entry, query) &&
        matchesRowFilters(entry, query),
    ),
  )
  for (const entry of listed) {
    const type = activityTypeOf(entry, inView)
    groupCounts.all += 1
    groupCounts[ACTIVITY_GROUP_OF[type]] += 1
    typeCounts[type] = (typeCounts[type] ?? 0) + 1
    if (matchesTypeFilters(type, query)) rows.push(entry)
  }
  return {
    rows: rows.sort(comparatorFor(query, inView)),
    groupCounts,
    typeCounts,
    totals: totalActivity(rows, inView),
    dateSpan: dateSpanOf(rows),
  }
}

export interface HiddenCopyCounts {
  // Cash legs of securities rows.
  ledgerRows: number
  // Securities rows typed Other whose ledger twin stays.
  securitiesRows: number
  // Oldest month first.
  secondStatementRows: { month: MonthKey; count: number }[]
}

const secondStatementCounts = (months: MonthKey[]) => {
  const counts = new Map<MonthKey, number>()
  for (const month of months) counts.set(month, (counts.get(month) ?? 0) + 1)
  return Array.from(counts, ([month, count]) => ({ month, count })).sort(
    (first, second) => compareText(first.month, second.month),
  )
}

// The rows hidden as repeats, in the accounts and dates the list covers.
export const hiddenCopyCounts = (
  hiddenCopies: ActivityEntry[],
  query: Pick<ActivityQuery, 'accountIds' | 'dateRange'>,
): HiddenCopyCounts => {
  const inView = accountFilterOf(query.accountIds)
  const counts = { ledgerRows: 0, securitiesRows: 0 }
  const secondStatementMonths: MonthKey[] = []
  for (const entry of hiddenCopies) {
    if (!inView(entry.accountId) || !isInDateRange(entry, query)) continue
    const reason = entry.hiddenAs
    if (reason?.kind === 'second_statement') {
      secondStatementMonths.push(reason.month)
    } else if (entry.source === 'cash_ledger') {
      counts.ledgerRows += 1
    } else {
      counts.securitiesRows += 1
    }
  }
  return {
    ...counts,
    secondStatementRows: secondStatementCounts(secondStatementMonths),
  }
}

const VIEWS_KEPT_PER_LIST = 8

const viewsByEntries = new WeakMap<ActivityEntry[], Map<string, ActivityView>>()

// Same rows and same filters give back the same view, so a re-render does no work.
export const activityViewFor = (
  entries: ActivityEntry[],
  query: ActivityQuery,
): ActivityView => {
  const views = viewsByEntries.get(entries) ?? new Map<string, ActivityView>()
  viewsByEntries.set(entries, views)
  const queryKey = JSON.stringify(query)
  const cached = views.get(queryKey)
  if (cached) return cached
  if (views.size >= VIEWS_KEPT_PER_LIST) views.clear()
  const view = buildActivityView(entries, query)
  views.set(queryKey, view)
  return view
}
